import React, { useState, useEffect, useMemo } from 'react';
import '../styles/AlertDashboard.css';
import logo from '../images/247Logo.png';
import { apiFetch, apiFetchJSON } from '../api/apiClient';
import FleetHeader from './FleetHeader';
import { usePageTour } from './tour/tourStore';

function formatStarlinkAlertMessage(alert) {
  const detail = alert.detail || '';
    
  if (detail.toLowerCase().includes('upload bandwidth restricted')) {
    return 'Upload Bandwidth Overage';
  } else if (detail.toLowerCase().includes('download bandwidth restricted')) {
    return 'Download Bandwidth Overage';
  } else if (detail.toLowerCase().includes('upload')) {
    return 'Upload Overage';
  } else if (detail.toLowerCase().includes('download')) {
    return 'Download Overage';
  }
    
  return 'Bandwidth Overage';
}

// Detect SF VPN clients (client_name starting with "Sf vpn", case-insensitive)
function isSfVpnAlert(alert) {
  if (!alert || alert.item_type !== 'client') return false;
  const name = (alert.client_name || '').trim().toLowerCase();
  return name.startsWith('sf vpn');
}

// One derived colour ramp (mockup 3a/4a) for the alert-TYPE breakdown -
// WAN/Client/Usage Cap/Starlink/SF VPN each get a step down the same
// brightness ramp rather than five unrelated brand hues, so the donut/
// stat-strip reads as "share of one whole" at a glance. This is scoped
// to alert-TYPE identity only; the from/to status chips keep their own
// separate semantic colors (green=good, red=bad, etc. via getStatusColor)
// since collapsing those into the same ramp would erase real meaning.
const ALERT_RAMP = {
  wan: '#88F4EB',
  client: '#CAFB6D',
  usageCap: '#4E8C93',
  starlink: '#D7D7CC',
  sfvpn: 'rgba(255,255,255,0.75)',
};

const ALERT_RAMP_ORDER = ['wan', 'client', 'usageCap', 'starlink', 'sfvpn'];

const ALERT_RAMP_LABELS = {
  wan: 'WAN Status',
  client: 'Client Status',
  usageCap: 'Usage Cap',
  starlink: 'Starlink Overage',
  sfvpn: 'SF VPN Status',
};

// Matches mockup 3a's hand-coded donut exactly: r=15.9155 makes the
// circle's circumference exactly 100 units, so each slice's
// stroke-dasharray can just be its percentage of the total directly,
// no unit conversion needed. Replaces the Chart.js <Pie> that used to
// render this - same real data (labels/values/colors), just drawn by
// hand instead of through a charting library.
function AlertDonut({ values, colors }) {
  const total = values.reduce((a, b) => a + b, 0);
  let cumulative = 0;
  return (
    <svg width="120" height="120" viewBox="0 0 42 42" style={{ display: 'block', flexShrink: 0, transform: 'rotate(-90deg)' }}>
      {values.map((v, i) => {
        const pct = total > 0 ? (v / total) * 100 : 0;
        const offset = -cumulative;
        cumulative += pct;
        if (pct <= 0) return null;
        return (
          <circle
            key={i}
            cx="21" cy="21" r="15.9"
            fill="none"
            stroke={colors[i]}
            strokeWidth="10"
            strokeDasharray={`${pct} ${100 - pct}`}
            strokeDashoffset={offset}
          />
        );
      })}
    </svg>
  );
}

function netmaskToCidr(netmask) {
    // Add validation at the beginning
    if (!netmask || typeof netmask !== 'string') {
        return ''; // or return '/0' depending on your needs
    }
    
    // Convert netmask from dotted decimal to binary string
    const binary = netmask.split('.')
        .map(octet => parseInt(octet, 10).toString(2).padStart(8, '0'))
        .join('');
    
    // Count the number of 1's in the binary string
    const cidr = (binary.match(/1/g) || []).length;
    
    // Validate that it's a valid netmask (all 1's followed by all 0's)
    if (!/^1+0+$/.test(binary)) {
        throw new Error('Invalid netmask: ' + netmask);
    }
    
    return '/' + cidr;
}

const AlertDashboard = ({ user = null, userType = 'admin', onLogout }) => {
  const [clientBadges, setClientBadges] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [starlinkAlerts, setStarlinkAlerts] = useState([]);
  const [vlanAlerts, setVlanAlerts] = useState([]);
  const [wanCapAlerts, setWanCapAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterDeviceType, setFilterDeviceType] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [quickFilter, setQuickFilter] = useState('all');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const PAGE_SIZE = 50;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const shouldHideStarlink = userType === 'client' || !!user?.skip_starlink;

  useEffect(() => {
      fetchBadges();
  }, []);

  const fetchBadges = async () => {
      const data = await apiFetchJSON('/api/client-badges');
      if (data) setClientBadges(data);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine if user is client
  const isClient = userType === 'client';

  const parseUTCTimeString = (timestamp) => {
    if (!timestamp) {
      // console.log('[parseUTCTimeString] empty/falsy timestamp:', timestamp);
      return null;
    }

    // If it's already a Date object or number, return it
    if (timestamp instanceof Date) return timestamp;
    if (typeof timestamp === 'number') return new Date(timestamp * 1000);

    if (typeof timestamp !== 'string') {
      // console.log('[parseUTCTimeString] unexpected type:', typeof timestamp, timestamp);
      return null;
    }

    // If it's a string with Z or timezone, parse normally
    if (timestamp.includes('Z') || timestamp.includes('+')) {
      const d = new Date(timestamp);
      // if (isNaN(d.getTime())) {
      //    console.log('[parseUTCTimeString] INVALID DATE (Z/+ branch). raw=', timestamp);
      // }
      return d;
    }

    // Parse UTC time string (assuming format: "YYYY-MM-DD HH:MM:SS" or
    // "YYYY-MM-DD HH:MM:SS.ffffff"). Safari/JavaScriptCore (real iOS
    // devices/browsers) is much stricter than Chrome/V8 about this: it
    // rejects fractional seconds longer than 3 digits and any stray
    // whitespace, where V8 quietly tolerates them. That mismatch is why
    // this can pass on a laptop (including Chrome's mobile-view emulator,
    // which still runs V8) but produce "Invalid Date" on a real iPhone.
    try {
      let normalized = timestamp.trim();

      // Truncate fractional seconds to 3 digits (JS Date only supports ms).
      // e.g. "2026-09-18 14:32:05.123456" -> "2026-09-18 14:32:05.123"
      normalized = normalized.replace(/(\.\d{3})\d+/, '$1');

      // Replace ALL spaces with 'T' (original code only replaced the first
      // one, which left a stray space in place for anything with more than
      // one space segment).
      normalized = normalized.replace(/ /g, 'T');

      const isoString = normalized.endsWith('Z') ? normalized : normalized + 'Z';
      const d = new Date(isoString);

      // if (isNaN(d.getTime())) {
      //   console.log('[parseUTCTimeString] INVALID DATE (manual branch). raw=', timestamp, 'normalized=', isoString);
      // }

      return d;
    } catch (error) {
      console.error('[parseUTCTimeString] threw while parsing:', timestamp, error);
      return null;
    }
  };

  const formatInLocalTime = (timestamp) => {
    const date = parseUTCTimeString(timestamp);
    if (!date || isNaN(date.getTime())) return null;
    
    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(date);
  };

  const formatDateTime = (timestamp) => {
    const formatted = formatInLocalTime(timestamp);
    if (!formatted) return 'Unknown';
    
    // Convert "11 Jan 2026, 18:50" to "11 Jan 2026 18:50"
    return formatted.replace(',', '');
  };

  // Get status color using new color scheme
  const getStatusColor = (status) => {
    if (!status) return { bg: 'rgba(202, 251, 109, 0.18)', text: 'var(--color-accent)', accent: 'var(--color-accent)' };
       
    const statusLower = status.toLowerCase();
    const goodKeywords = ['online', 'connected', 'ready', 'active', 'running', 'up', 'normal'];
    // Overage/failure states read as "bad" the same as disconnected - both
    // get the solid white chip (mockup 3a shows "SIGNAL THRESHOLD FAIL" and
    // "DISCONNECTED" with the identical white-bg/navy-text treatment, not
    // a separate purple for overage).
    const badKeywords = [
      'offline', 'disconnected', 'disabled', 'outage', 
      'failed', 'no active', 'no cable', 'booting', 
      'no device','search', 'down', 'unknown', 'overage warning', 'overage'
    ];

    const GOOD = { bg: 'rgba(136, 244, 235, 0.16)', text: 'var(--color-secondary)', accent: 'var(--color-secondary)' };
    const BAD = { bg: '#fff', text: 'var(--color-primary)', accent: '#fff' };
    const STANDBY = { bg: 'rgba(202, 251, 109, 0.18)', text: 'var(--color-accent)', accent: 'var(--color-accent)' };

    if (goodKeywords.includes(statusLower)) return GOOD;
    if (badKeywords.includes(statusLower)) return BAD;
    
    for (const keyword of badKeywords) {
      if (statusLower.includes(keyword)) return BAD;
    }
    for (const keyword of goodKeywords) {
      if (statusLower.includes(keyword)) return GOOD;
    }
    
    // Anything else (standby, etc.) - matches 3a's neon STANDBY chip
    return STANDBY;
  };

  // Format status display
  const formatStatusDisplay = (status) => {
    if (!status) return "Unknown";
    if (status.toLowerCase() in ['n/a', 'unknown', '']) {
      return status;
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Format time
  const formatTime = (timestamp) => {
    const date = parseUTCTimeString(timestamp);
    if (!date || isNaN(date.getTime())) return 'N/A';
    
    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).format(date);
  };

  const formatShortTime = (timestamp) => {
    const date = parseUTCTimeString(timestamp);
    if (!date || isNaN(date.getTime())) {
      // console.log('[formatShortTime] could not format, raw timestamp=', timestamp);
      return 'N/A';
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(date);
  };

  // formatShortTime() normally returns "18 Sep, 14:32" and callers grab the
  // time half with .split(',')[1].trim(). If parsing failed, formatShortTime
  // instead returns the plain string 'N/A' (no comma), so
  // 'N/A'.split(',')[1] is undefined and .trim() on undefined throws
  // "undefined is not an object" - this was the crash on iOS. These two
  // small helpers do the same split but fall back safely instead of
  // crashing.
  const formatShortDatePart = (timestamp) => {
    const full = formatShortTime(timestamp);
    const parts = full.split(',');
    return parts.length > 1 ? parts[0].trim() : full;
  };

  const formatShortTimePart = (timestamp) => {
    const full = formatShortTime(timestamp);
    const parts = full.split(',');
    return parts.length > 1 ? parts[1].trim() : full;
  };

  // Get IP address for alert
  const getAlertIp = (alert) => {
    if (alert.ip) return alert.ip;
    if (alert.item_type === 'client') return alert.client_ip || '';
    if (alert.item_type === 'wan_interface') return alert.interface_ip || '';
    if (alert.item_type === 'starlink') return '';
    if (alert.alert_category === 'vlan') return alert.vlan_ip || '';
    if (alert.alert_category === 'wancap') return alert.wan_ip || '';
    return '';
  };

  // Get device badge based on device type
  const getDeviceBadge = (alert) => {

    const badgeConfig = clientBadges[alert.organization_id];
    if (badgeConfig) {
      return {
        label: badgeConfig.badge_name,
        backgroundColor: badgeConfig.badge_color,
        color: badgeConfig.badge_font_color,
        icon: 'fa-building'
      };
    }
    
    switch(alert.device_type) {
      case 'fully_managed':
        return {
          label: 'FM',
          backgroundColor: 'rgba(136, 244, 235, 0.16)', 
          color: 'var(--color-secondary)',
        };
      case 'wan_managed':
        return {
          label: 'WM',
          backgroundColor: 'rgba(136, 244, 235, 0.16)', 
          color: 'var(--color-secondary)',
        };
      case 'data_center':
        return {
          label: 'DC',
          backgroundColor: 'rgba(136, 244, 235, 0.16)', 
          color: 'var(--color-secondary)',
        };
      default:
        return {
          label: '?',
          backgroundColor: 'rgba(255, 255, 255, 0.12)', 
          color: 'rgba(255, 255, 255, 0.7)',
        };
    }
  };

  // Truncate text with ellipsis
  const truncateText = (text, maxLength = 20) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Load all alerts
  const loadAlerts = async () => {
    try {
      setLoading(true);
      
      // Fetch all three alert types in parallel
      const promises = [];
      
      // 1. Fetch importance alerts
      const importanceEndpoint = isClient ? '/api/client/importance/alerts?limit=500' : '/api/importance/alerts?limit=500';
      promises.push(
        apiFetch(importanceEndpoint).then(res => res.json()).then(data => ({
          type: 'importance',
          alerts: (data.alerts || []).map(a => ({ 
            ...a, 
            alert_category: 'status',
            alert_time: a.alert_time
          }))
        })).catch(err => ({ type: 'importance', alerts: [], error: err }))
      );
      
      // 2. Fetch starlink alerts (if not hidden)
      if (!shouldHideStarlink) {
        const starlinkEndpoint = isClient ? '/api/client/starlink/alerts?limit=200' : '/api/starlink/alerts?limit=200';
        promises.push(
          apiFetch(starlinkEndpoint).then(res => res.json()).then(data => ({
            type: 'starlink',
            alerts: (data.alerts || []).map(a => ({ 
              ...a, 
              alert_category: 'starlink',
              item_type: 'starlink',
              from_status: 'normal',
              to_status: 'overage',
              alert_time: a.alert_time
            }))
          })).catch(err => ({ type: 'starlink', alerts: [], error: err }))
        );
      } else {
        promises.push(Promise.resolve({ type: 'starlink', alerts: [] }));
      }
      
      // 3. Fetch VLAN alerts
      const vlanEndpoint = '/api/vlan/alerts?limit=500';
      promises.push(
        apiFetch(vlanEndpoint).then(res => res.json()).then(data => ({
          type: 'vlan',
          alerts: (data.alerts || []).map(alert => ({
            ...alert,
            alert_category: 'vlan',
            item_type: 'vlan',
            alert_time: alert.alert_time,
            from_status: 'normal',
            to_status: alert.alert_type === 'critical' 
              ? `Overage warning: ${alert.usage_percentage}% (${alert.usage_gb} GB / ${alert.cap_gb} GB)` 
              : `Usage Reminder: ${alert.usage_percentage}% (${alert.usage_gb} GB / ${alert.cap_gb} GB)`,
            device_name: alert.device_name,
            device_type: alert.device_type,
            serial_number: alert.serial_number,
            organization_id: alert.organization_id,
            item_key: `VLAN ${alert.vlan_id}`,
            client_name: alert.vlan_name,
            ip: alert.vlan_ip,
            usage_percentage: alert.usage_percentage,
            usage_gb: alert.usage_gb,
            cap_gb: alert.cap_gb
          }))
        })).catch(err => ({ type: 'vlan', alerts: [], error: err }))
      );

      // 4. Fetch WAN cap alerts (usage caps on WAN uplinks).
      //    NOTE: alert_category 'wancap' is deliberately distinct from the
      //    'wan_interface' STATUS alerts above. WAN cap alerts are grouped with
      //    VLAN caps under the combined "Usage Cap" metric, but keep a WAN badge.
      const wanCapEndpoint = '/api/wan/alerts?limit=500';
      promises.push(
        apiFetch(wanCapEndpoint).then(res => res.json()).then(data => ({
          type: 'wancap',
          alerts: (data.alerts || []).map(alert => ({
            ...alert,
            alert_category: 'wancap',
            item_type: 'wancap',
            alert_time: alert.alert_time,
            from_status: 'normal',
            to_status: alert.alert_type === 'critical'
              ? `Overage warning: ${alert.usage_percentage}% (${alert.usage_gb} GB / ${alert.cap_gb} GB)`
              : `Usage Reminder: ${alert.usage_percentage}% (${alert.usage_gb} GB / ${alert.cap_gb} GB)`,
            device_name: alert.device_name,
            device_type: alert.device_type,
            serial_number: alert.serial_number,
            organization_id: alert.organization_id,
            item_key: `WAN ${alert.wan_id}`,
            client_name: alert.wan_name,
            ip: alert.wan_ip,
            usage_percentage: alert.usage_percentage,
            usage_gb: alert.usage_gb,
            cap_gb: alert.cap_gb
          }))
        })).catch(err => ({ type: 'wancap', alerts: [], error: err }))
      );
      
      // Wait for all promises to resolve
      const results = await Promise.all(promises);
      
      // Extract alerts from results
      const importanceAlerts = results.find(r => r.type === 'importance')?.alerts || [];
      const starlinkAlertsResult = results.find(r => r.type === 'starlink')?.alerts || [];
      const vlanAlertsResult = results.find(r => r.type === 'vlan')?.alerts || [];
      const wanCapAlertsResult = results.find(r => r.type === 'wancap')?.alerts || [];
      
      // Find the oldest importance alert date
      let oldestImportanceDate = null;
      if (importanceAlerts.length > 0) {
        const importanceDates = importanceAlerts
          .map(alert => parseUTCTimeString(alert.alert_time))
          .filter(date => date && !isNaN(date.getTime()));
        
        if (importanceDates.length > 0) {
          oldestImportanceDate = new Date(Math.min(...importanceDates));
        }
      }
      
      // Filter starlink alerts - keep only those newer than oldest importance date
      let filteredStarlinkAlerts = starlinkAlertsResult;
      if (oldestImportanceDate && starlinkAlertsResult.length > 0) {
        filteredStarlinkAlerts = starlinkAlertsResult.filter(alert => {
          const alertDate = parseUTCTimeString(alert.alert_time);
          return alertDate && alertDate >= oldestImportanceDate;
        });
      }
      
      // Filter VLAN alerts - keep only those newer than oldest importance date
      let filteredVlanAlerts = vlanAlertsResult;
      if (oldestImportanceDate && vlanAlertsResult.length > 0) {
        filteredVlanAlerts = vlanAlertsResult.filter(alert => {
          const alertDate = parseUTCTimeString(alert.alert_time);
          return alertDate && alertDate >= oldestImportanceDate;
        });
      }

      // Filter WAN cap alerts - keep only those newer than oldest importance date
      let filteredWanCapAlerts = wanCapAlertsResult;
      if (oldestImportanceDate && wanCapAlertsResult.length > 0) {
        filteredWanCapAlerts = wanCapAlertsResult.filter(alert => {
          const alertDate = parseUTCTimeString(alert.alert_time);
          return alertDate && alertDate >= oldestImportanceDate;
        });
      }
      
      // Combine all alerts
      const combinedAlerts = [
        ...importanceAlerts,
        ...filteredStarlinkAlerts,
        ...filteredVlanAlerts,
        ...filteredWanCapAlerts
      ];
      
      // Sort all alerts by time (oldest first, then newest for display)
      combinedAlerts.sort((a, b) => {
        const dateA = parseUTCTimeString(a.alert_time);
        const dateB = parseUTCTimeString(b.alert_time);
        return dateA - dateB; // Oldest first
      });
      
      // Set the alerts in state
      setAlerts(importanceAlerts);
      setStarlinkAlerts(filteredStarlinkAlerts);
      setVlanAlerts(filteredVlanAlerts);
      setWanCapAlerts(filteredWanCapAlerts);
      
      // For backward compatibility, also set a combined alerts array
      // You might want to create a new state for this or use getAllAlerts useMemo
      
      setLastUpdated(formatDateTime(new Date()));
      
      // Log the filtering results for debugging
      // console.log('Alert filtering results:', {
      //   importanceCount: importanceAlerts.length,
      //   starlinkOriginal: starlinkAlertsResult.length,
      //   starlinkFiltered: filteredStarlinkAlerts.length,
      //   vlanOriginal: vlanAlertsResult.length,
      //   vlanFiltered: filteredVlanAlerts.length,
      //   oldestImportanceDate: oldestImportanceDate?.toISOString(),
      //   totalCombined: combinedAlerts.length
      // });
      
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractFromParentheses = (text) => {
    if (!text) return '';
    const match = text.match(/\((.*?)\)/);
    return match ? match[1] : text;
  };

  // Get all alerts combined (for total view)
  const getAllAlerts = useMemo(() => {
    // Combine all alerts, but ensure they're sorted by time
    const all = [...alerts, ...starlinkAlerts, ...vlanAlerts, ...wanCapAlerts];
    all.sort((a, b) => {
      const dateA = parseUTCTimeString(a.alert_time);
      const dateB = parseUTCTimeString(b.alert_time);
      return dateB - dateA; // Newest first for display
    });
    return all;
  }, [alerts, starlinkAlerts, vlanAlerts, wanCapAlerts]);

  // Apply filters to any alert list
  const applyFilters = (alertList) => {
    let result = [...alertList];
    
    // Apply quick filter (last 1 hour)
    if (quickFilter === '1hour') {
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
      result = result.filter(alert => {
        const alertDate = parseUTCTimeString(alert.alert_time);
        return alertDate > oneHourAgo;
      });
    }
    
    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(alert => {
        return (
          (alert.serial_number && alert.serial_number.toLowerCase().includes(query)) ||
          (alert.device_name && alert.device_name.toLowerCase().includes(query)) ||
          (alert.item_key && alert.item_key.toLowerCase().includes(query)) ||
          (alert.client_name && alert.client_name.toLowerCase().includes(query)) ||
          (alert.interface_ip && alert.interface_ip.toLowerCase().includes(query)) ||
          (alert.client_ip && alert.client_ip.toLowerCase().includes(query)) ||
          (alert.wan_name && alert.wan_name.toLowerCase().includes(query)) ||
          (alert.detail && alert.detail.toLowerCase().includes(query)) ||
          (alert.vlan_name && alert.vlan_name.toLowerCase().includes(query)) ||
          (alert.wan_ip && alert.wan_ip.toLowerCase().includes(query))
        );
      });
    }
    
    // Apply type filter
    if (filterType !== 'all') {
      result = result.filter(alert => {
        if (filterType === 'client') return alert.item_type === 'client' && !isSfVpnAlert(alert);
        if (filterType === 'sfvpn') return isSfVpnAlert(alert);
        if (filterType === 'wan') return alert.item_type === 'wan_interface';
        if (filterType === 'starlink') return alert.alert_category === 'starlink';
        // "Usage Cap" combines VLAN caps and WAN caps into one filter
        if (filterType === 'vlan') return alert.alert_category === 'vlan' || alert.alert_category === 'wancap';
        return true;
      });
    }
    
    // Apply device type filter
    if (filterDeviceType !== 'all') {
      result = result.filter(alert => alert.device_type === filterDeviceType);
    }
    
    // Apply date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      let cutoffDate;
      
      switch(dateFilter) {
        case 'today':
          cutoffDate = new Date(now.setHours(0, 0, 0, 0));
          break;
        case 'yesterday':
          cutoffDate = new Date(now.setDate(now.getDate() - 1));
          cutoffDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoffDate = new Date(now.setDate(now.getDate() - 7));
          break;
        case 'month':
          cutoffDate = new Date(now.setMonth(now.getMonth() - 1));
          break;
        default:
          break;
      }
      
      if (cutoffDate) {
        result = result.filter(alert => {
          const alertDate = parseUTCTimeString(alert.alert_time);
          return alertDate > cutoffDate;
        });
      }
    }
    
    // Sort by time (newest first)
    result.sort((a, b) => {
      const dateA = parseUTCTimeString(a.alert_time);
      const dateB = parseUTCTimeString(b.alert_time);
      return dateB - dateA;
    });
    
    return result;
  };

  // Get filtered alerts based on current filter settings
  const filteredAlerts = useMemo(() => {
    return applyFilters(getAllAlerts);
  }, [getAllAlerts, searchQuery, filterType, filterDeviceType, dateFilter, quickFilter]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchQuery, filterType, filterDeviceType, dateFilter, quickFilter]);

  const visibleAlerts = filteredAlerts.slice(0, visibleCount);

  // Analytics data for charts
  const analyticsData = useMemo(() => {
    // Count by alert type using the filtered alerts
    const statusClientCount = alerts.filter(a => a.item_type === 'client' && !isSfVpnAlert(a)).length;
    const sfVpnCount = alerts.filter(a => isSfVpnAlert(a)).length;
    const statusWanCount = alerts.filter(a => a.item_type === 'wan_interface').length;
    const starlinkCount = starlinkAlerts.length;
    const vlanCount = vlanAlerts.length;
    const wanCapCount = wanCapAlerts.length;
    // Combined metric shown on the dashboard counter + chart
    const usageCapCount = vlanCount + wanCapCount;

    // One derived ramp, ordered WAN / Client / Usage Cap / (Starlink) / SF VPN
    // - matches the stat strip above and the mobile summary bar, so the
    // same category always reads as the same step of the ramp everywhere
    // on the page (mockup 3a/4a).
    const rampCounts = { wan: statusWanCount, client: statusClientCount, usageCap: usageCapCount, starlink: starlinkCount, sfvpn: sfVpnCount };
    const rampKeysShown = ALERT_RAMP_ORDER.filter(key => key !== 'starlink' || !shouldHideStarlink);

    const distributionData = {
      labels: rampKeysShown.map(key => ALERT_RAMP_LABELS[key]),
      datasets: [{
        label: 'Alert Count',
        data: rampKeysShown.map(key => rampCounts[key]),
        backgroundColor: rampKeysShown.map(key => ALERT_RAMP[key]),
        borderColor: rampKeysShown.map(key => ALERT_RAMP[key]),
        borderWidth: 1,
      }],
    };

    // Device type distribution using getAllAlerts - ordered WAN Managed,
    // Fully Managed, Data Centre and colored off the same ramp (mockup 3a).
    const wanManagedCount = getAllAlerts.filter(a => a.device_type === 'wan_managed').length;
    const fullyManagedCount = getAllAlerts.filter(a => a.device_type === 'fully_managed').length;
    const dataCenterCount = getAllAlerts.filter(a => a.device_type === 'data_center').length;

    const deviceChartData = {
      labels: isClient
        ? ['WAN Managed', 'Fully Managed']
        : ['WAN Managed', 'Fully Managed', 'Data Centre'],
      datasets: [
        {
          label: 'Alert Count',
          data: isClient
            ? [wanManagedCount, fullyManagedCount]
            : [wanManagedCount, fullyManagedCount, dataCenterCount],
          backgroundColor: isClient
            ? [ALERT_RAMP.wan, ALERT_RAMP.client]
            : [ALERT_RAMP.wan, ALERT_RAMP.client, ALERT_RAMP.starlink],
          borderColor: isClient
            ? [ALERT_RAMP.wan, ALERT_RAMP.client]
            : [ALERT_RAMP.wan, ALERT_RAMP.client, ALERT_RAMP.starlink],
          borderWidth: 1,
        },
      ],
    };

    // Top 10 Devices by Alert Count
    const deviceAlertCounts = {};
    getAllAlerts.forEach(alert => {
      const deviceName = alert.device_name || alert.serial_number;
      deviceAlertCounts[deviceName] = (deviceAlertCounts[deviceName] || 0) + 1;
    });

    const sortedDevices = Object.entries(deviceAlertCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);

    const topDeviceNames = sortedDevices.map(([name]) => name);
    const topDeviceCounts = sortedDevices.map(([, count]) => count);

    // Rank-tiered ramp for the bar list - top third teal, middle third the
    // muted usage-cap step, remainder grey (mockup 3a bars fade the same way).
    const rankTierColor = (index, total) => {
      const third = Math.max(1, Math.ceil(total / 3));
      if (index < third) return ALERT_RAMP.wan;
      if (index < third * 2) return ALERT_RAMP.usageCap;
      return ALERT_RAMP.starlink;
    };
    const topDeviceColors = topDeviceNames.map((_, i) => rankTierColor(i, topDeviceNames.length));

    const topDevicesBarData = {
      labels: topDeviceNames,
      datasets: [
        {
          label: 'Number of Alerts',
          data: topDeviceCounts,
          backgroundColor: topDeviceColors,
          borderColor: topDeviceColors,
          borderWidth: 1,
        },
      ],
    };

    // ── WINDOW / RATE / TOP TYPE (under "By Alert Type") ──
    // WINDOW just names whichever time filter is active; RATE and the
    // dominant-type callout are both derived from the alerts actually
    // loaded, rather than inventing a trend line with no historical
    // baseline to compare against.
    const windowLabel =
      quickFilter === '1hour' ? '1 HOUR' :
      dateFilter === 'today' ? 'TODAY' :
      dateFilter === 'yesterday' ? 'YESTERDAY' :
      dateFilter === 'week' ? '7 DAYS' :
      dateFilter === 'month' ? '30 DAYS' : 'ALL TIME';

    const alertTimes = getAllAlerts
      .map(a => parseUTCTimeString(a.alert_time))
      .filter(d => d && !isNaN(d.getTime()))
      .map(d => d.getTime());
    const spanMinutes = alertTimes.length > 1
      ? Math.max(1, (Math.max(...alertTimes) - Math.min(...alertTimes)) / 60000)
      : 1;
    const ratePerMin = getAllAlerts.length > 0 ? getAllAlerts.length / spanMinutes : 0;

    let topRampKey = 'wan';
    let topRampCount = -1;
    rampKeysShown.forEach(key => {
      if (rampCounts[key] > topRampCount) { topRampCount = rampCounts[key]; topRampKey = key; }
    });
    const topRampPct = getAllAlerts.length > 0 ? Math.round((topRampCount / getAllAlerts.length) * 100) : 0;
    const typeInsight = getAllAlerts.length > 0
      ? `${ALERT_RAMP_LABELS[topRampKey]} dominates the window — ${topRampCount} of ${getAllAlerts.length} events are ${ALERT_RAMP_LABELS[topRampKey].toLowerCase()}.`
      : 'No alerts in the current window.';

    // ── DEVICES / ALERTS PER DEVICE / NOISIEST 5 (under "By Device Class") ──
    const uniqueDeviceCount = Object.keys(deviceAlertCounts).length;
    const alertsPerDevice = uniqueDeviceCount > 0 ? (getAllAlerts.length / uniqueDeviceCount) : 0;
    const top5Sum = topDeviceCounts.slice(0, 5).reduce((a, b) => a + b, 0);
    const noisiest5Pct = getAllAlerts.length > 0 ? Math.round((top5Sum / getAllAlerts.length) * 100) : 0;

    const wanManagedDeviceCount = new Set(
      getAllAlerts.filter(a => a.device_type === 'wan_managed').map(a => a.device_name || a.serial_number)
    ).size;
    const fullyManagedDeviceCount = new Set(
      getAllAlerts.filter(a => a.device_type === 'fully_managed').map(a => a.device_name || a.serial_number)
    ).size;
    const wanManagedPerDevice = wanManagedDeviceCount > 0 ? wanManagedCount / wanManagedDeviceCount : 0;
    const fullyManagedPerDevice = fullyManagedDeviceCount > 0 ? fullyManagedCount / fullyManagedDeviceCount : 0;
    const managedRatio = fullyManagedPerDevice > 0 ? wanManagedPerDevice / fullyManagedPerDevice : 0;
    const deviceInsight = (wanManagedDeviceCount > 0 && fullyManagedDeviceCount > 0 && managedRatio > 1.1)
      ? `WAN-managed kit generates ${managedRatio.toFixed(1)}× the alerts per device of fully managed — tuning your noisiest devices would cut the queue fastest.`
      : uniqueDeviceCount > 0
        ? `${uniqueDeviceCount} devices are generating alerts right now, averaging ${alertsPerDevice.toFixed(1)} each.`
        : 'No devices have generated alerts in the current window.';

    return { 
      distributionData,
      deviceChartData, 
      topDevicesBarData,
      totalAlerts: getAllAlerts.length,
      statusClientCount,
      sfVpnCount,
      statusWanCount,
      starlinkCount,
      vlanCount,
      wanCapCount,
      usageCapCount,
      windowLabel,
      ratePerMin,
      topRampKey,
      topRampPct,
      typeInsight,
      uniqueDeviceCount,
      alertsPerDevice,
      noisiest5Pct,
      deviceInsight,
    };
  }, [alerts, starlinkAlerts, vlanAlerts, wanCapAlerts, getAllAlerts, isClient, shouldHideStarlink, quickFilter, dateFilter]);

  // Auto-refresh
  useEffect(() => {
    loadAlerts();
    const interval = setInterval(loadAlerts, 180000);
    return () => clearInterval(interval);
  }, [isClient, shouldHideStarlink]);

  // Handle back to dashboard
  const handleBackToDashboard = () => {
    if (isClient) {
      window.location.href = '/dashboard';
    } else {
      window.location.href = '/admin/dashboard';
    }
  };

  // Handle logout
  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setFilterType('all');
    setFilterDeviceType('all');
    setDateFilter('all');
    setQuickFilter('all');
  };

  // ---- Guided tour ("?" in FleetHeader) ---------------------------------
  const tourFirst = visibleAlerts[0] || null;
  const tourFirstType = !tourFirst ? null
    : tourFirst.alert_category === 'vlan' ? { label: 'VLAN', cls: 'alert-type-vlan', kind: 'usageCap' }
    : tourFirst.alert_category === 'wancap' ? { label: 'WAN', cls: 'alert-type-vlan', kind: 'usageCap' }
    : tourFirst.alert_category === 'starlink' ? { label: 'Starlink', cls: 'alert-type-starlink', kind: 'starlink' }
    : isSfVpnAlert(tourFirst) ? { label: 'SF VPN', cls: 'alert-type-sfvpn', kind: 'sfvpn' }
    : tourFirst.item_type === 'client' ? { label: 'Client', cls: 'alert-type-client', kind: 'client' }
    : { label: 'WAN', cls: 'alert-type-wan', kind: 'wan' };
  usePageTour('alerts', {
    isMobile,
    totalAlerts: analyticsData.totalAlerts,
    windowLabel: analyticsData.windowLabel,
    topTypeLabel: ALERT_RAMP_LABELS[analyticsData.topRampKey],
    topTypePct: analyticsData.topRampPct,
    topDeviceName: analyticsData.topDevicesBarData.labels[0],
    topDeviceCount: analyticsData.topDevicesBarData.datasets[0].data[0],
    uniqueDeviceCount: analyticsData.uniqueDeviceCount,
    firstAlert: tourFirst && {
      device: tourFirst.device_name || tourFirst.serial_number,
      type: tourFirstType,
    },
    shouldHideStarlink,
    onTourStart: () => setSelectedAlert(null),
  }, { ready: !loading });

  const summary = {
    total: alerts.length,
    clients: alerts.filter(a => a.item_type === 'client').length,
    wan: alerts.filter(a => a.item_type === 'wan_interface').length,
    vlan: vlanAlerts.length + wanCapAlerts.length,
    starlink: shouldHideStarlink ? 0 : starlinkAlerts.length,
  };

  const getColor = (title) => {
    if (title === "Total") return "var(--color-label)";
    if (title === "Clients") return "var(--color-accent)";
    if (title === "WAN") return "var(--color-secondary)";
    if (title === "Starlink") return "var(--color-starlink)";
    if (title === "VLAN") return "var(--color-download)";
    return "var(--color-muted)";
  };

  // Mobile Summary Card Component
  const MobileAlertCard = ({ alert, onClick, tourId }) => {
    const statusColor = getStatusColor(alert.to_status);
    const isStarlink = alert.alert_category === 'starlink';
    const isVlan = alert.alert_category === 'vlan';
    const isWanCap = alert.alert_category === 'wancap';
    const isUsageCap = isVlan || isWanCap; // combined VLAN + WAN cap
    const badge = getDeviceBadge(alert);

    const truncateMobile = (text, maxLength = 25) => {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    };
    
    const netmask = alert.netmask ? netmaskToCidr(alert.netmask) : '';
    const alertIp = getAlertIp(alert);

    const getDetailColumn = () => {
      if (isUsageCap && alertIp) {
        return <div className="mobile-ip">IP {alertIp}{netmask}</div>;
      }
      if (alertIp) {
        return <div className="mobile-ip">IP {alertIp}</div>;
      }
      if (isStarlink) {
        return '';
      }
      return '';
    };

    return (
      <div className="mobile-alert-card" onClick={() => onClick(alert)} data-tour={tourId}>
        <div className="mobile-status-bar" style={{ backgroundColor: statusColor.accent }}></div>
        <div className="mobile-alert-content">
          <div className="mobile-alert-top-row">
            <span className="mobile-time">{formatShortTimePart(alert.alert_time)}</span>
            <span className="mobile-device-name">
              {
                isStarlink 
                  ? alert.wan_name 
                  : (alert.device_name || alert.serial_number)
              }
            </span>
          </div>

          <div className="mobile-item-name">
            {isVlan ? `${alert.vlan_name} (${alert.vlan_ip})` : isWanCap ? alert.wan_name : isStarlink ? alert.serial_number : alert.item_type === 'wan_interface' ? alert.item_key : alert.client_name}
            <span className="mobile-item-sep"> · </span>
            <span className={`mobile-type-text ${isUsageCap ? 'vlan-text' : isStarlink ? 'starlink-text' : alert.item_type === 'wan_interface' ? 'wan-text' : 'client-text'}`}>
              {isVlan ? 'VLAN' : isWanCap ? 'WAN' : isStarlink ? 'Starlink' : alert.item_type === 'wan_interface' ? 'WAN' : 'CLIENT'}
            </span>
          </div>

          {getDetailColumn()}

          <div className="mobile-status-change">
            <span className="mobile-from-status">
              {isStarlink ? 'Normal' : isUsageCap ? 'Normal' : formatStatusDisplay(alert.from_status)}
            </span>
            <span className="mobile-arrow">→</span>
            <span className="mobile-to-status" style={{ backgroundColor: statusColor.bg, color: statusColor.text }}>
              {isStarlink
                ? formatStarlinkAlertMessage(alert)
                : isUsageCap
                  ? formatStatusDisplay(truncateMobile(alert.to_status, 20))
                  : formatStatusDisplay(truncateMobile(alert.to_status, 20))}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const MobileDetailModal = ({ alert, onClose }) => {
    if (!alert) return null;
    const isStarlink = alert.alert_category === 'starlink';
    const isVlan = alert.alert_category === 'vlan';
    const isWanCap = alert.alert_category === 'wancap';
    const isUsageCap = isVlan || isWanCap;
    const badge = getDeviceBadge(alert);
    const alertType = isVlan ? 'VLAN' : isWanCap ? 'WAN' : isStarlink ? 'Starlink' : alert.item_type === 'wan_interface' ? 'WAN' : 'Client';
    
    // Get display text for item
    const getItemDisplayText = () => {
      if (isVlan) return alert.vlan_name;
      if (isWanCap) return alert.wan_name;
      if (isStarlink) return alert.wan_name;
      if (alert.item_type === 'client') return alert.client_name;
      return alert.item_key;
    };
    
    // Get device display text
    const getDeviceDisplayText = () => {
      const deviceName = alert.device_name || '';
      const serialNumber = alert.serial_number || '';
      if (deviceName && serialNumber) {
        return `${deviceName} (${serialNumber})`;
      }
      return deviceName || serialNumber || 'N/A';
    };
    
    // Get formatted IP with netmask
    const getFormattedIpWithNetmask = () => {
      const ip = getAlertIp(alert);
      if (!ip) return null;
      
      if (alert.netmask) {
        const cidr = netmaskToCidr(alert.netmask);
        return `${ip}${cidr}`;
      }
      return ip;
    };
    
    // Get status text
    const getToStatusText = () => {
      if (isStarlink) return formatStarlinkAlertMessage(alert) || 'Overage';
      if (isUsageCap) return alert.to_status;
      return formatStatusDisplay(alert.to_status);
    };
    
    const getFromStatusText = () => {
      if (isStarlink || isUsageCap) return 'Normal';
      return formatStatusDisplay(alert.from_status);
    };
    
    const formattedIp = getFormattedIpWithNetmask();
    
    return (
      <div className="mobile-modal-overlay" onClick={onClose}>
        <div className="mobile-modal-content" onClick={e => e.stopPropagation()}>
          <div className="mobile-modal-header">
            <h3>Alert Details</h3>
            <button className="mobile-modal-close" onClick={onClose}>×</button>
          </div>
          
          <div className="mobile-modal-body">
            {/* Alert Type Section */}
            <div className="mobile-modal-section">
              <label className="mobile-modal-label">Alert Type</label>
              <div className="mobile-modal-value">
                <span className={`mobile-modal-type-badge ${isUsageCap ? 'vlan-badge' : isStarlink ? 'starlink-badge' : alert.item_type === 'wan_interface' ? 'wan-badge' : 'client-badge'}`}>
                  {alertType}
                </span>
              </div>
            </div>
            
            {/* Item Name Section */}
            <div className="mobile-modal-section">
              <label className="mobile-modal-label">
                {isVlan ? 'VLAN Name' : isWanCap ? 'WAN Name' : isStarlink ? 'WAN Interface' : alert.item_type === 'client' ? 'Client Name' : 'WAN Interface'}
              </label>
              <div className="mobile-modal-value">
                {getItemDisplayText() || 'N/A'}
              </div>
            </div>
            
            {/* Device Details Section */}
            <div className="mobile-modal-section">
              <label className="mobile-modal-label">Device Details</label>
              <div className="mobile-modal-value">
                {getDeviceDisplayText()}
              </div>
            </div>
            
            {/* VLAN Specific Information */}
            {isVlan && (
              <>
                <div className="mobile-modal-section">
                  <label className="mobile-modal-label">VLAN ID</label>
                  <div className="mobile-modal-value">{alert.vlan_id}</div>
                </div>
                
                <div className="mobile-modal-section">
                  <label className="mobile-modal-label">Usage</label>
                  <div className="mobile-modal-value">
                    {alert.usage_gb?.toFixed(2)} GB / {alert.cap_gb?.toFixed(2)} GB ({alert.usage_percentage}%)
                  </div>
                  {/* Optional: Add progress bar for usage */}
                  <div className="mobile-modal-progress-bar">
                    <div 
                      className="mobile-modal-progress-fill" 
                      style={{ 
                        width: `${Math.min(alert.usage_percentage || 0, 100)}%`,
                        backgroundColor: (alert.usage_percentage || 0) > 90 ? '#fff' : 'var(--color-accent)'
                      }}
                    />
                  </div>
                </div>
              </>
            )}
            
            {/* WAN Cap Specific Information */}
            {isWanCap && (
              <>
                <div className="mobile-modal-section">
                  <label className="mobile-modal-label">WAN ID</label>
                  <div className="mobile-modal-value">{alert.wan_id}</div>
                </div>
                
                <div className="mobile-modal-section">
                  <label className="mobile-modal-label">Usage</label>
                  <div className="mobile-modal-value">
                    {alert.usage_gb?.toFixed(2)} GB / {alert.cap_gb?.toFixed(2)} GB ({alert.usage_percentage}%)
                  </div>
                  <div className="mobile-modal-progress-bar">
                    <div 
                      className="mobile-modal-progress-fill" 
                      style={{ 
                        width: `${Math.min(alert.usage_percentage || 0, 100)}%`,
                        backgroundColor: (alert.usage_percentage || 0) > 90 ? '#fff' : 'var(--color-accent)'
                      }}
                    />
                  </div>
                </div>
              </>
            )}
            
            {/* Associated Client (for non-client items) */}
            {alert.item_type !== 'client' && alert.client_name && 
             !isStarlink && !isUsageCap && (
              <div className="mobile-modal-section">
                <label className="mobile-modal-label">Associated Client</label>
                <div className="mobile-modal-value">{alert.client_name}</div>
              </div>
            )}
            
            {/* IP Address */}
            {formattedIp && (
              <div className="mobile-modal-section">
                <label className="mobile-modal-label">
                  {isVlan ? 'VLAN IP Address' : isWanCap ? 'WAN IP Address' : 'IP Address'}
                </label>
                <div className="mobile-modal-value">
                  <code>{formattedIp}</code>
                </div>
              </div>
            )}
            
            {/* Status Change Section */}
            <div className="mobile-modal-section">
              <label className="mobile-modal-label">Status Change</label>
              <div className="mobile-modal-value">
                <span style={{ color: getStatusColor(getFromStatusText()).accent }}>
                  {getFromStatusText()}
                </span>
                <span> → </span>
                <span style={{ color: getStatusColor(getToStatusText()).accent, fontWeight: 'bold' }}>
                  {getToStatusText()}
                </span>
              </div>
            </div>
            
            {/* Additional Details */}
            {alert.detail && (
              <div className="mobile-modal-section">
                <label className="mobile-modal-label">Details</label>
                <div className="mobile-modal-value mobile-modal-detail-text">
                  {alert.detail}
                </div>
              </div>
            )}
            
            {/* Alert Time */}
            <div className="mobile-modal-section">
              <label className="mobile-modal-label">Alert Time</label>
              <div className="mobile-modal-value">{formatTime(alert.alert_time)}</div>
            </div>
            
            {/* Acknowledged Information */}
            {alert.acknowledged === 1 && (
              <div className="mobile-modal-section">
                <label className="mobile-modal-label">Acknowledged By</label>
                <div className="mobile-modal-value">
                  {alert.acknowledged_by || 'Unknown'} at {formatTime(alert.acknowledged_at)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="alert-dashboard">
        <FleetHeader
          title="Alert Dashboard"
          user={user}
          onLogout={onLogout}
          scope={isClient ? 'client' : 'admin'}
        />
      {/* Mobile View */}
      {isMobile ? (
        <>
          {/* Total + derived-ramp segmented bar (mockup 4a) */}
          <div className="mobile-ramp-summary" data-tour="al-summary">
            <button
              className={`mobile-ramp-total ${filterType === 'all' ? 'active' : ''}`}
              onClick={() => { setFilterType('all'); setSearchQuery(''); }}
            >
              <div className="mobile-ramp-total-label">Total</div>
              <div className="mobile-ramp-total-number">{analyticsData.totalAlerts}</div>
            </button>
            <div className="mobile-ramp-bar-wrap">
              <div className="mobile-ramp-bar">
                {ALERT_RAMP_ORDER.filter(key => key !== 'starlink' || !shouldHideStarlink).map(key => {
                  const countByKey = {
                    wan: analyticsData.statusWanCount,
                    client: analyticsData.statusClientCount,
                    usageCap: analyticsData.usageCapCount,
                    starlink: analyticsData.starlinkCount,
                    sfvpn: analyticsData.sfVpnCount,
                  }[key];
                  return (
                    <span
                      key={key}
                      style={{ flex: Math.max(countByKey, 0.0001), background: ALERT_RAMP[key] }}
                    />
                  );
                })}
              </div>
              <div className="mobile-ramp-rate">
                {analyticsData.ratePerMin.toFixed(1)} /min · <span className="mobile-ramp-rate-top">{analyticsData.topRampPct}% {ALERT_RAMP_LABELS[analyticsData.topRampKey]}</span>
              </div>
            </div>
          </div>

          {/* Category mini-grid, same ramp order as everywhere else on the page */}
          <div className="mobile-ramp-grid" data-tour="al-categories">
            {[
              { key: 'wan', label: 'WAN', count: analyticsData.statusWanCount, filterKey: 'wan' },
              { key: 'client', label: 'Client', count: analyticsData.statusClientCount, filterKey: 'client' },
              { key: 'usageCap', label: 'Usage Cap', count: analyticsData.usageCapCount, filterKey: 'vlan' },
              ...(shouldHideStarlink ? [] : [{ key: 'starlink', label: 'Overage', count: analyticsData.starlinkCount, filterKey: 'starlink' }]),
              // { key: 'sfvpn', label: 'SF VPN', count: analyticsData.sfVpnCount, filterKey: 'sfvpn' },
            ].map(cell => (
              <button
                key={cell.key}
                className={`mobile-ramp-grid-cell ${filterType === cell.filterKey ? 'active' : ''}`}
                onClick={() => { setFilterType(cell.filterKey); setSearchQuery(''); }}
              >
                <div className="mobile-ramp-grid-top">
                  <span className="mobile-ramp-grid-dot" style={{ background: ALERT_RAMP[cell.key] }} />
                  <span className="mobile-ramp-grid-label">{cell.label}</span>
                </div>
                <div className="mobile-ramp-grid-count">{cell.count}</div>
              </button>
            ))}
          </div>

          {/* Filter Bar - New for mobile */}
          <div className="mobile-filter-bar" data-tour="al-filters">
            <button 
              className={`mobile-filter-chip ${quickFilter === '1hour' ? 'active' : ''}`}
              onClick={() => setQuickFilter(quickFilter === '1hour' ? 'all' : '1hour')}
            >
              Last Hour
            </button>
            
            <select 
              className="mobile-filter-select"
              value={filterDeviceType}
              onChange={(e) => setFilterDeviceType(e.target.value)}
            >
              <option value="all">All Devices</option>
              <option value="fully_managed">Fully Managed</option>
              <option value="wan_managed">WAN Managed</option>
              {!isClient && <option value="data_center">Data Center</option>}
            </select>
            
            <select 
              className="mobile-filter-select"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
          </div>

          {/* Search Input */}
          <div className="mobile-search-container" data-tour="al-search">
            <i className="fas fa-search mobile-search-icon"></i>
            <input
              type="text"
              className="mobile-search-input"
              placeholder="Search alerts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="mobile-search-clear" onClick={() => setSearchQuery('')}>
                ×
              </button>
            )}
          </div>

          {/* Active Filters Banner */}
          {(filterType !== 'all' || filterDeviceType !== 'all' || dateFilter !== 'all' || quickFilter !== 'all' || searchQuery) && (
            <div className="mobile-filter-banner">
              <span>
                Filters active
                {filterType !== 'all' && ` • ${formatStatusDisplay(filterType)}`}
                {filterDeviceType !== 'all' && ` • ${formatStatusDisplay(filterDeviceType)}`}
                {dateFilter !== 'all' && ` • ${formatStatusDisplay(dateFilter)}`}
                {quickFilter === '1hour' && ' • Last hour'}
              </span>
              <button className="mobile-clear-filter" onClick={clearFilters}>
                CLEAR ALL
              </button>
            </div>
          )}

          {/* Alerts List */}
          <div className="mobile-alerts-list">
            {loading ? (
              <div className="mobile-loading">
                <div className="spinner-border" style={{ color: 'var(--color-secondary)' }} role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : filteredAlerts.length === 0 ? (
              <div className="mobile-empty-state">
                <p>No alerts found</p>
                <button className="mobile-clear-all-filters" onClick={clearFilters}>
                  Clear all filters
                </button>
              </div>
            ) : (
              <>
                {visibleAlerts.map((alert, idx) => (
                  <MobileAlertCard
                    key={`${alert.alert_category}-${alert.id}`}
                    alert={alert}
                    onClick={setSelectedAlert}
                    tourId={idx === 0 ? 'al-first' : undefined}
                  />
                ))}
                {filteredAlerts.length > visibleAlerts.length ? (
                  <button
                    className="mobile-load-more"
                    onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                  >
                    +{filteredAlerts.length - visibleAlerts.length} more this window
                  </button>
                ) : (
                  <div className="mobile-load-more-done">
                    {visibleAlerts.length} of {filteredAlerts.length} shown
                  </div>
                )}
              </>
            )}
          </div>

          {/* Detail Modal */}
          <MobileDetailModal
            alert={selectedAlert}
            onClose={() => setSelectedAlert(null)}
          />
        </>
      ) : (
        /* Desktop View */
        <>
          <div className="container mt-4">
            {/* Summary strip - one derived colour ramp instead of five
                unrelated brand hues, so proportion reads at a glance
                (mockup 3a). Each cell's mini-bar width is that category's
                share of the (unfiltered) total. */}
            <div className="ramp-stat-strip" data-tour="al-summary">
              <button
                className={`ramp-stat-cell ramp-stat-total ${filterType === 'all' ? 'ramp-stat-selected' : ''}`}
                onClick={() => setFilterType('all')}
              >
                <div className="ramp-stat-label">Total Alerts</div>
                <div className="ramp-stat-number">{analyticsData.totalAlerts}</div>
                <div className="ramp-stat-sub">{analyticsData.windowLabel}</div>
              </button>

              {ALERT_RAMP_ORDER.filter(key => key !== 'starlink' || !shouldHideStarlink).map(key => {
                const countByKey = {
                  wan: analyticsData.statusWanCount,
                  client: analyticsData.statusClientCount,
                  usageCap: analyticsData.usageCapCount,
                  starlink: analyticsData.starlinkCount,
                  sfvpn: analyticsData.sfVpnCount,
                }[key];
                const filterKeyByRamp = { wan: 'wan', client: 'client', usageCap: 'vlan', starlink: 'starlink', sfvpn: 'sfvpn' };
                const pct = analyticsData.totalAlerts > 0 ? (countByKey / analyticsData.totalAlerts) * 100 : 0;
                return (
                  <button
                    key={key}
                    className={`ramp-stat-cell ${filterType === filterKeyByRamp[key] ? 'ramp-stat-selected' : ''}`}
                    onClick={() => setFilterType(filterKeyByRamp[key])}
                  >
                    <div className="ramp-stat-label">{ALERT_RAMP_LABELS[key]}</div>
                    <div className="ramp-stat-number" style={{ color: ALERT_RAMP[key] }}>{countByKey}</div>
                    <div className="ramp-stat-bar-track">
                      <div className="ramp-stat-bar-fill" style={{ width: `${pct}%`, background: ALERT_RAMP[key] }} />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Charts */}
            <div className="alert-panels-row" data-tour="al-charts">
              <div className="alert-chart-card">
                <div className="alert-panel-title">BY ALERT TYPE</div>
                <div className="alert-donut-row">
                  <AlertDonut
                    values={ALERT_RAMP_ORDER.filter(key => key !== 'starlink' || !shouldHideStarlink).map(key => ({
                      wan: analyticsData.statusWanCount,
                      client: analyticsData.statusClientCount,
                      usageCap: analyticsData.usageCapCount,
                      starlink: analyticsData.starlinkCount,
                      sfvpn: analyticsData.sfVpnCount,
                    }[key]))}
                    colors={ALERT_RAMP_ORDER.filter(key => key !== 'starlink' || !shouldHideStarlink).map(key => ALERT_RAMP[key])}
                  />
                  <div className="alert-donut-legend">
                    {ALERT_RAMP_ORDER.filter(key => key !== 'starlink' || !shouldHideStarlink).map(key => {
                      const countByKey = {
                        wan: analyticsData.statusWanCount,
                        client: analyticsData.statusClientCount,
                        usageCap: analyticsData.usageCapCount,
                        starlink: analyticsData.starlinkCount,
                        sfvpn: analyticsData.sfVpnCount,
                      }[key];
                      const pct = analyticsData.totalAlerts > 0 ? ((countByKey / analyticsData.totalAlerts) * 100).toFixed(1) : '0.0';
                      return (
                        <div className="alert-legend-row" key={key}>
                          <span className="alert-legend-dot" style={{ background: ALERT_RAMP[key] }} />
                          <span className="alert-legend-label">{ALERT_RAMP_LABELS[key]}</span>
                          <span className="alert-legend-pct">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="alert-mini-grid">
                  <div className="alert-mini-cell">
                    <div className="alert-mini-label">WINDOW</div>
                    <div className="alert-mini-value">{analyticsData.windowLabel}</div>
                  </div>
                  <div className="alert-mini-cell">
                    <div className="alert-mini-label">RATE</div>
                    <div className="alert-mini-value">{analyticsData.ratePerMin.toFixed(1)} /min</div>
                  </div>
                  <div className="alert-mini-cell">
                    <div className="alert-mini-label">VS 24H AVG</div>
                    <div className="alert-mini-value" style={{ color: ALERT_RAMP[analyticsData.topRampKey] }}>
                      {analyticsData.topRampPct}%
                    </div>
                  </div>
                </div>
                <p className="alert-panel-insight">{analyticsData.typeInsight}</p>
              </div>

              <div className="alert-chart-card">
                <div className="alert-panel-title">BY DEVICE CLASS</div>
                <div className="alert-donut-row">
                  <AlertDonut
                    values={analyticsData.deviceChartData.datasets[0].data}
                    colors={analyticsData.deviceChartData.datasets[0].backgroundColor}
                  />
                  <div className="alert-donut-legend">
                    {analyticsData.deviceChartData.labels.map((label, i) => (
                      <div className="alert-legend-row" key={label}>
                        <span className="alert-legend-dot" style={{ background: analyticsData.deviceChartData.datasets[0].backgroundColor[i] }} />
                        <span className="alert-legend-label">{label}</span>
                        <span className="alert-legend-pct">{analyticsData.deviceChartData.datasets[0].data[i]}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="alert-mini-grid">
                  <div className="alert-mini-cell">
                    <div className="alert-mini-label">DEVICES</div>
                    <div className="alert-mini-value">{analyticsData.uniqueDeviceCount}</div>
                  </div>
                  <div className="alert-mini-cell">
                    <div className="alert-mini-label">ALERTS / DEVICE</div>
                    <div className="alert-mini-value">{analyticsData.alertsPerDevice.toFixed(1)}</div>
                  </div>
                  <div className="alert-mini-cell">
                    <div className="alert-mini-label">NOISIEST 5</div>
                    <div className="alert-mini-value" style={{ color: ALERT_RAMP.client }}>{analyticsData.noisiest5Pct}%</div>
                  </div>
                </div>
                <p className="alert-panel-insight">{analyticsData.deviceInsight}</p>
              </div>

              <div className="alert-chart-card">
                <div className="alert-panel-title-row">
                  <span className="alert-panel-title">TOP DEVICES BY ALERT COUNT</span>
                  <span className="alert-panel-subtitle">TOP 10</span>
                </div>
                <div className="alert-bar-list">
                  {analyticsData.topDevicesBarData.labels.length > 0 ? (
                    (() => {
                      const maxCount = Math.max(...analyticsData.topDevicesBarData.datasets[0].data);
                      return analyticsData.topDevicesBarData.labels.map((label, i) => {
                        const count = analyticsData.topDevicesBarData.datasets[0].data[i];
                        const color = analyticsData.topDevicesBarData.datasets[0].backgroundColor[i];
                        const widthPct = maxCount > 0 ? (count / maxCount) * 100 : 0;
                        return (
                          <div className="alert-bar-row" key={label}>
                            <span className="alert-bar-label" title={label}>{label}</span>
                            <div className="alert-bar-track">
                              <div className="alert-bar-fill" style={{ width: `${widthPct}%`, background: color }} />
                            </div>
                            <span className="alert-bar-count">{count}</span>
                          </div>
                        );
                      });
                    })()
                  ) : (
                    <div className="alert-empty-state" style={{ height: '100%' }}>
                      <p>No alert data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
              
            {/* Search and Filter Controls */}
            <div className="alert-toolbar" data-tour="al-filters">
              <div className="alert-search-box">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder="device · item · IP · VLAN"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <select 
                className="alert-toolbar-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">ALL TYPES</option>
                <option value="client">CLIENT STATUS</option>
                <option value="sfvpn">SF VPN STATUS</option>
                <option value="wan">WAN STATUS</option>
                <option value="vlan">USAGE CAP</option>
                {!shouldHideStarlink && <option value="starlink">STARLINK OVERAGE</option>}
              </select>

              <select 
                className="alert-toolbar-select"
                value={filterDeviceType}
                onChange={(e) => setFilterDeviceType(e.target.value)}
              >
                <option value="all">ALL DEVICES</option>
                <option value="fully_managed">FULLY MANAGED</option>
                <option value="wan_managed">WAN MANAGED</option>
                {!isClient && <option value="data_center">DATA CENTER</option>}
              </select>

              <select 
                className="alert-toolbar-select"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <option value="all">ALL TIME</option>
                <option value="today">TODAY</option>
                <option value="yesterday">YESTERDAY</option>
                <option value="week">LAST 7 DAYS</option>
                <option value="month">LAST 30 DAYS</option>
              </select>

              <button 
                className={'alert-toolbar-btn' + (quickFilter === '1hour' ? ' active' : '')}
                onClick={() => setQuickFilter(quickFilter === '1hour' ? 'all' : '1hour')}
              >
                {quickFilter === '1hour' ? 'CLEAR 1H' : 'LAST 1 HOUR'}
              </button>

              <button 
                className="alert-toolbar-text-btn"
                onClick={clearFilters}
              >
                CLEAR
              </button>

              <div className="alert-toolbar-spacer">
                <span className="alert-toolbar-count">
                  {filteredAlerts.length} OF {analyticsData.totalAlerts}
                </span>
                <button 
                  className="alert-toolbar-refresh"
                  onClick={loadAlerts}
                  disabled={loading}
                >
                  REFRESH <i className="fas fa-refresh"></i>
                </button>
              </div>
            </div>
            {(searchQuery || filterType !== 'all' || filterDeviceType !== 'all' || dateFilter !== 'all' || quickFilter === '1hour') && (
              <div className="alert-toolbar-detail">
                Showing {filteredAlerts.length} of {analyticsData.totalAlerts} alerts
                {searchQuery && ` matching "${searchQuery}"`}
                {filterType !== 'all' && ` • Type: ${filterType}`}
                {filterDeviceType !== 'all' && ` • Device: ${filterDeviceType}`}
                {dateFilter !== 'all' && ` • Date: ${dateFilter}`}
                {quickFilter === '1hour' && ` • Last hour only`}
              </div>
            )}

            {/* Alerts Table */}
            <div className="alert-table-card">
              <div className="card-body p-0">
                {loading ? (
                  <div className="alert-loading-container">
                    <div className="alert-loading-spinner spinner-border" style={{ color: 'var(--color-secondary)' }} role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading alerts...</p>
                  </div>
                ) : filteredAlerts.length === 0 ? (
                  <div className="alert-empty-state">
                    <h5>No alerts found</h5>
                    <p className="text-muted">
                      {isClient ? 'No alerts for your routers' : 'Try adjusting your search or filters'}
                    </p>
                  </div>
                ) : (
                  <div className="alert-grid-wrap">
                    <div className="alert-grid-header">
                      <div>TIME</div><div>DEVICE</div><div>TYPE</div><div>ITEM</div><div>IP ADDRESS</div><div>STATUS CHANGE</div>
                    </div>
                    {visibleAlerts.map((alert, idx) => {
                          const fromColor = getStatusColor(alert.from_status);
                          const toColor = getStatusColor(alert.to_status);
                          const alertIp = getAlertIp(alert);
                          const netmask = alert.netmask ? netmaskToCidr(alert.netmask) : '';
                          const isStarlink = alert.alert_category === 'starlink';
                          const isVlan = alert.alert_category === 'vlan';
                          const isWanCap = alert.alert_category === 'wancap';
                          const isUsageCap = isVlan || isWanCap;
                          const badge = getDeviceBadge(alert);
                          const isSFVPN = isSfVpnAlert(alert);
                          
                          // Get alert type display name
                          const getAlertTypeDisplay = () => {
                            if (isVlan) return 'VLAN';
                            if (isWanCap) return 'WAN';
                            if (isStarlink) return 'Starlink';
                            if (isSfVpnAlert(alert)) return 'SF VPN';
                            if (alert.item_type === 'client') return 'Client';
                            if (alert.item_type === 'wan_interface') return 'WAN';
                            return 'Unknown';
                          };
                          
                          // For usage-cap alerts, show usage details
                          const getDetailColumn = () => {
                            if (isUsageCap) {
                              return <span className="text-muted">{alertIp}{netmask}</span>;
                            }
                            if (alertIp) {
                              return <code className="alert-ip-address">{alertIp}</code>;
                            }
                            if (isStarlink) {
                              return <span className="text-muted">-</span>;
                            }
                            return <span className="text-muted">N/A</span>;
                          };
                          
                          return (
                            <div className={'alert-grid-row' + (idx % 2 === 1 ? ' alert-grid-row-alt' : '')} key={`${alert.alert_category}-${alert.id}`} data-tour={idx === 0 ? 'al-first' : undefined}>
                              <div>
                                <div className="alert-time-main">{formatShortTimePart(alert.alert_time)}</div>
                                <div className="alert-time-secondary">
                                  {formatTime(alert.alert_time).split(',')[0]}
                                </div>
                              </div>
                              <div>
                                <div className="alert-device-name">
                                  {isStarlink ? alert.wan_name : (alert.device_name || alert.serial_number)}
                                </div>
                                <div className="alert-device-info">
                                  <span className="device-type-chip" style={{ backgroundColor: badge.backgroundColor, color: badge.color }}>
                                    {badge.label}
                                  </span>
                                  {alert.serial_number}
                                </div>
                              </div>
                              <div>
                                <span className={`alert-type-chip ${isUsageCap ? 'alert-type-vlan' : isStarlink ? 'alert-type-starlink' : isSFVPN ? 'alert-type-sfvpn' : alert.item_type === 'client' ? 'alert-type-client' : 'alert-type-wan'}`}>
                                  {getAlertTypeDisplay()}
                                </span>
                              </div>
                              <div className="alert-item-cell" title={isVlan ? alert.vlan_name : isWanCap ? alert.wan_name : isStarlink ? alert.wan_name : alert.item_type === 'client' ? alert.client_name : alert.item_key}>
                                <div className="alert-item-name">
                                  {truncateText(isVlan ? alert.vlan_name : isWanCap ? alert.wan_name : isStarlink ? extractFromParentheses(alert.detail) : alert.item_type === 'client' ? alert.client_name : alert.item_key, 28)}
                                </div>
                                {isVlan && (
                                  <div className="alert-item-sub">VLAN ID: {alert.vlan_id}</div>
                                )}
                                {isWanCap && (
                                  <div className="alert-item-sub">WAN ID: {alert.wan_id}</div>
                                )}
                                {alert.item_type === 'client' && alert.item_key && (
                                  <div className="alert-item-sub" title={alert.item_key}>
                                    {truncateText(alert.item_key, 25)}
                                  </div>
                                )}
                              </div>
                              <div>{getDetailColumn()}</div>
                              <div className="alert-status-change">
                                <span 
                                  className="alert-status-chip"
                                  style={{ 
                                    backgroundColor: fromColor.bg,
                                    color: fromColor.text,
                                  }}
                                  title={isStarlink || isUsageCap ? 'Normal' : formatStatusDisplay(alert.from_status || 'Unknown')}
                                >
                                  {truncateText(isStarlink || isUsageCap ? 'Normal' : formatStatusDisplay(alert.from_status || 'Unknown'), 30)}
                                </span>
                                <span className="alert-status-arrow">→</span>
                                <span 
                                  className="alert-status-chip"
                                  style={{ 
                                    backgroundColor: toColor.bg,
                                    color: toColor.text,
                                  }}
                                  title={isStarlink ? formatStarlinkAlertMessage(alert) || 'Overage' : isUsageCap ? `${alert.to_status}` : formatStatusDisplay(alert.to_status || 'Unknown')}
                                >
                                  {isStarlink 
                                    ? truncateText(formatStarlinkAlertMessage(alert) || 'Overage', 30)
                                    : isUsageCap 
                                      ? truncateText(alert.to_status, 36)
                                      : truncateText(formatStatusDisplay(alert.to_status || 'Unknown'), 30)
                                  }
                                </span>
                              </div>
                            </div>
                          );
                        })}
                  </div>
                )}
                {!loading && filteredAlerts.length > 0 && (
                  <div className="alert-table-footer">
                    <span className="alert-table-footer-count">
                      {visibleAlerts.length} OF {filteredAlerts.length} SHOWN
                    </span>
                    {filteredAlerts.length > visibleAlerts.length && (
                      <button
                        className="alert-table-load-more"
                        onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                      >
                        Load more →
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AlertDashboard;
